// Local-access key management — operator-authed shared-store keys.
//
// The remote gateway serves these on the production manager (X-Manager-Local-Key
// keys a LOCAL box uses to reach the remote shared store):
//   GET    /store/keys        -> { keys: LocalAccessKey[] }
//   POST   /store/keys        -> CreateKeyResponse  (raw key shown ONCE)
//   DELETE /store/keys/{id}   -> { ok: true }        (revoke)
// Create/revoke invalidate the list so the table reflects the new state.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../lib/apiContext';
import { queryKeys } from '../../lib/queryKeys';
import type {
  LocalKeysResponse,
  CreateKeyResponse,
  RevokeKeyResponse,
  LocalConfigStatus,
  LocalConfigConnectRequest,
  LocalConfigConnectResponse,
} from '../../lib/types';

/** GET /store/keys — list of local-access keys (no raw secrets). */
export function useLocalKeys() {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.localKeys,
    queryFn: () => client.get<LocalKeysResponse>('/store/keys'),
  });
}

/** POST /store/keys {label} — mint a key; the response carries the raw key ONCE. */
export function useCreateLocalKey() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      client.post<CreateKeyResponse>('/store/keys', { label }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.localKeys }),
  });
}

/** DELETE /store/keys/{id} — revoke (sets revoked_at); list is invalidated. */
export function useRevokeLocalKey() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete<RevokeKeyResponse>(`/store/keys/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.localKeys }),
  });
}

// -- KEY GATE (LOCAL box connecting to the remote shared store) --------------

/** GET /store/local-config/status — read on the local box to decide the KEY GATE. */
export function useLocalConfigStatus() {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.localConfigStatus,
    queryFn: () => client.get<LocalConfigStatus>('/store/local-config/status'),
  });
}

/** POST /store/local-config — save the pasted key; the box connects to the remote store. */
export function useConnectRemote() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LocalConfigConnectRequest) =>
      client.post<LocalConfigConnectResponse>('/store/local-config', body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.localConfigStatus }),
  });
}
